import { useState } from "react";
import { motion } from "framer-motion";
import {
  RefreshCw, CheckCircle2, AlertTriangle, Clock, ChevronDown, Loader2,
} from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// FIN-7 — BankConnectionCard
//
// Card individual de uma conexão Pluggy. Status pill + accounts_count +
// botão refresh + botão desconectar + footer pills (sync history).
//
// Props:
//   connection         — { id, institution_name, institution_logo_url,
//                          accounts_count, status, last_synced_at,
//                          next_sync_at, sync_history[] }
//   onRefresh(id)      — handler do botão refresh (opcional)
//   onDisconnect(id)   — handler do botão DESCONECTAR (opcional)
//   disabled           — desabilita ações quando flag off (estado dormente)

const STATUS_META = {
  updated:        { Icon: CheckCircle2, label: "Atualizado",     tone: "success" },
  updating:       { Icon: Loader2,      label: "Atualizando",    tone: "info"    },
  outdated:       { Icon: Clock,        label: "Desatualizado",  tone: "warning" },
  error:          { Icon: AlertTriangle,label: "Erro",           tone: "error"   },
  login_required: { Icon: AlertTriangle,label: "Reautorizar",    tone: "warning" },
  disconnected:   { Icon: AlertTriangle,label: "Desconectado",   tone: "muted"   },
};

const TONE_CLASSES = {
  success: "text-status-success bg-status-success/10 border-status-success/30",
  info:    "text-brand-cyan      bg-brand-cyan/10      border-brand-cyan/30",
  warning: "text-status-warning  bg-status-warning/10  border-status-warning/30",
  error:   "text-status-error    bg-status-error/10    border-status-error/30",
  muted:   "text-foreground-muted bg-surface-card-hover border-subtle",
};

function _relativeFromNow(iso) {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const ms = dt.getTime() - Date.now();
  const abs = Math.abs(ms);
  const min = Math.round(abs / 60000);
  const hour = Math.round(min / 60);
  const day = Math.round(hour / 24);
  const past = ms < 0;
  if (min < 60) {
    return past ? `há ${min} min` : `em ${min} min`;
  }
  if (hour < 24) {
    return past ? `há ${hour} ${hour === 1 ? "hora" : "horas"}` : `em cerca de ${hour} ${hour === 1 ? "hora" : "horas"}`;
  }
  return past ? `há ${day} ${day === 1 ? "dia" : "dias"}` : `em ${day} ${day === 1 ? "dia" : "dias"}`;
}

function _todaySyncCount(history = []) {
  if (!Array.isArray(history) || history.length === 0) return null;
  const todayKey = new Date().toISOString().slice(0, 10);
  const today = history.filter((h) => {
    const at = h?.at;
    if (!at) return false;
    return String(at).startsWith(todayKey);
  });
  if (today.length === 0) return null;
  const ok = today.filter((h) => h.event === "updated").length;
  return `${ok}/${today.length} hoje`;
}

function BankAvatar({ logoUrl, name }) {
  const [imgErr, setImgErr] = useState(false);
  const fallback = (name || "??").slice(0, 4).toUpperCase();
  if (logoUrl && !imgErr) {
    return (
      <div className="w-12 h-12 rounded-xl bg-surface-card-hover border border-subtle flex items-center justify-center overflow-hidden shrink-0">
        <img
          src={logoUrl}
          alt={`Logo ${name}`}
          className="w-full h-full object-contain"
          onError={() => setImgErr(true)}
        />
      </div>
    );
  }
  return (
    <div
      className="w-12 h-12 rounded-xl bg-surface-card-hover border border-subtle flex items-center justify-center text-[10px] font-bold tracking-wider text-foreground-muted shrink-0"
      aria-hidden="true"
    >
      {fallback}
    </div>
  );
}

export default function BankConnectionCard({
  connection,
  onRefresh,
  onDisconnect,
  disabled = false,
}) {
  const status = connection?.status || "outdated";
  const meta = STATUS_META[status] || STATUS_META.outdated;
  const Icon = meta.Icon;
  const isUpdating = status === "updating";

  const todayCount = _todaySyncCount(connection?.sync_history);
  const lastSyncRel = _relativeFromNow(connection?.last_synced_at);
  const nextSyncRel = _relativeFromNow(connection?.next_sync_at);

  const accountsCount = connection?.accounts_count ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
    >
      <Card
        variant="elevated"
        className="glass-card p-4 md:p-5 hover:shadow-glow-green/30 transition-all duration-fast"
        data-testid={`conexoes-bank-card-${connection?.id || "unknown"}`}
      >
        <div className="flex items-start justify-between gap-4">
          {/* Left: avatar + name + accounts count */}
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <BankAvatar
              logoUrl={connection?.institution_logo_url}
              name={connection?.institution_name}
            />
            <div className="min-w-0">
              <h4 className="font-semibold text-foreground text-base md:text-lg leading-tight truncate">
                {connection?.institution_name || "Banco"}
              </h4>
              <p className="text-caption text-foreground-muted mt-0.5">
                {accountsCount} {accountsCount === 1 ? "conta" : "contas"}
              </p>
            </div>
          </div>

          {/* Right: status pill + actions */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <Badge
              variant="outline"
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-caption font-medium border",
                TONE_CLASSES[meta.tone],
              )}
            >
              <Icon
                className={cn("w-3.5 h-3.5", isUpdating && "animate-spin")}
                aria-hidden="true"
              />
              {meta.label}
            </Badge>

            <button
              type="button"
              onClick={() => onRefresh && onRefresh(connection?.id)}
              disabled={disabled || isUpdating}
              className={cn(
                "w-9 h-9 rounded-lg border border-subtle flex items-center justify-center",
                "text-foreground-muted hover:text-brand-green hover:border-brand-green/40 hover:bg-brand-green/10",
                "transition-all duration-fast",
                "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground-muted disabled:hover:border-subtle",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green/60",
              )}
              aria-label={`Atualizar conexão ${connection?.institution_name}`}
              data-testid={`conexoes-bank-refresh-${connection?.id}`}
            >
              <RefreshCw className={cn("w-4 h-4", isUpdating && "animate-spin")} aria-hidden="true" />
            </button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onDisconnect && onDisconnect(connection)}
              disabled={disabled}
              className="border-status-error/40 text-status-error hover:bg-status-error/10 hover:text-status-error font-bold tracking-wider text-caption"
              data-testid={`conexoes-bank-disconnect-${connection?.id}`}
            >
              DESCONECTAR
            </Button>
          </div>
        </div>

        {/* Footer pills: sync history */}
        <div className="flex flex-wrap items-center gap-2 mt-3.5 pl-[60px]">
          {todayCount && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-status-success/10 text-status-success text-caption border border-status-success/20">
              <RefreshCw className="w-3 h-3" aria-hidden="true" />
              {todayCount}
            </span>
          )}
          {lastSyncRel && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-card-hover text-foreground-muted text-caption border border-subtle">
              <Clock className="w-3 h-3" aria-hidden="true" />
              Atualizado {lastSyncRel}
            </span>
          )}
          {nextSyncRel && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-status-success/10 text-status-success text-caption border border-status-success/20">
              <RefreshCw className="w-3 h-3" aria-hidden="true" />
              Próxima {nextSyncRel}
            </span>
          )}
        </div>
      </Card>
    </motion.div>
  );
}
